<script>
import { useAlert } from 'dashboard/composables';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import multiselect from 'vue-multiselect';

export default {
  components: {
    SettingsSection,
    NextButton,
    multiselect,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedAgents: [],
      voiceConfig: null,
    };
  },
  computed: {
    agentList() {
      return this.$store.getters['agents/getAgents'];
    },
    isVoiceChannel() {
      return this.inbox?.channel_type === 'Channel::Voice';
    },
    uiFlags() {
      return this.$store.getters['inboxMembers/getUIFlags'];
    },
    isFetching() {
      return this.uiFlags.isFetching;
    },
    isUpdating() {
      return this.uiFlags.isUpdating;
    },
    // Total de canais contratados (informação comercial)
    totalChannels() {
      return this.voiceConfig?.total_channels || 0;
    },
    hasSelectedAgents() {
      return this.selectedAgents.length > 0;
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      this.fetchAttachedAgents();
    },

    async fetchAttachedAgents() {
      if (!this.isVoiceChannel) return;

      try {
        const response = await this.$store.dispatch('inboxMembers/get', {
          inboxId: this.inbox.id,
        });

        const { data } = response;

        // Armazenar voice_config
        this.voiceConfig = data.data?.voice_config || null;

        // Carregar agentes já selecionados
        const currentAgentIds = data.data?.agent_ids || [];
        this.selectedAgents = this.agentList.filter(agent =>
          currentAgentIds.includes(agent.id)
        );
      } catch (error) {
        console.error('Erro ao carregar agentes:', error);
        useAlert('Erro ao carregar agentes');
      }
    },

    async updateAgents() {
      if (!this.isVoiceChannel || this.isUpdating) return;

      try {
        if (!this.hasSelectedAgents) {
          useAlert('Selecione pelo menos um agente');
          return;
        }

        const payload = {
          inboxId: this.inbox.id,
          agentIds: this.selectedAgents.map(a => a.id),
        };

        await this.$store.dispatch('inboxMembers/update', payload);
        useAlert(this.$t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));


        // Recarregar dados para garantir sincronização
        await this.fetchAttachedAgents();
      } catch (error) {
        console.error('Erro ao atualizar:', error);
        const errorMessage = error.response?.data?.error || this.$t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    },
  },
};
</script>

<template>
  <div v-if="isVoiceChannel">
    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_AGENTS')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_AGENTS_SUB_TEXT')"
    >
      <!-- Loading -->
      <div v-if="isFetching" class="flex items-center justify-center py-8">
        <span class="text-n-slate-11">Carregando...</span>
      </div>

      <div v-else>
        <!-- Info de Canais Contratados -->
        <div
          v-if="voiceConfig && totalChannels > 0"
          class="mb-4 p-3 bg-n-alpha-1 border border-n-weak rounded-lg"
        >
          <div class="flex items-center gap-2 text-sm text-n-slate-11">
            <svg
              class="w-4 h-4 text-n-slate-11"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>
              Este canal possui
              <strong class="text-n-slate-12">{{ totalChannels }} canais simultâneos</strong>
              contratados.
            </span>
          </div>
          <p class="mt-1 text-xs text-n-slate-10 ml-6">
            Você pode adicionar quantos agentes quiser. Os canais determinam apenas quantas ligações podem ocorrer ao mesmo tempo.
          </p>
        </div>

        <!-- Seleção de Agentes -->
        <div class="mb-4">
          <label class="block">
            <span class="text-sm font-medium text-n-slate-12 mb-2 block">
              Agentes com acesso ao canal de voz
              <span class="text-n-ruby-9">*</span>
            </span>
            <multiselect
              v-model="selectedAgents"
              :options="agentList"
              track-by="id"
              label="name"
              multiple
              :close-on-select="false"
              :clear-on-select="false"
              hide-selected
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
              placeholder="Selecione os agentes..."
              class="voice-agents-multiselect"
            >
              <template #option="{ option }">
                <div class="flex items-center gap-2">
                  <span class="font-medium">{{ option.name }}</span>
                  <span class="text-xs text-n-slate-10">{{ option.email }}</span>
                </div>
              </template>
              <template #tag="{ option, remove }">
                <span class="multiselect__tag">
                  <span>{{ option.name }}</span>
                  <i
                    class="multiselect__tag-icon"
                    @click="remove(option)"
                  />
                </span>
              </template>
            </multiselect>
          </label>

          <!-- Status de Validação -->
          <div class="mt-3 flex items-center gap-2">
            <svg
              :class="hasSelectedAgents ? 'text-n-teal-11' : 'text-n-amber-11'"
              class="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                v-if="hasSelectedAgents"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
              <path
                v-else
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p
              :class="hasSelectedAgents ? 'text-n-teal-11' : 'text-n-amber-11'"
              class="text-sm font-medium"
            >
              <template v-if="!hasSelectedAgents">
                Selecione pelo menos um agente
              </template>
              <template v-else>
                {{ selectedAgents.length }}
                {{ selectedAgents.length === 1 ? 'agente selecionado' : 'agentes selecionados' }}
              </template>
            </p>
          </div>
        </div>

        <!-- Explicação sobre endpoints -->
        <div class="mb-4 p-3 bg-n-slate-2 border border-n-slate-6 rounded-lg">
          <div class="flex items-start gap-2">
            <svg
              class="w-5 h-5 text-n-iris-11 flex-shrink-0 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
            <div class="text-sm text-n-slate-11">
              <p class="font-medium text-n-slate-12 mb-1">
                Como funciona?
              </p>
              <p>
                Cada agente selecionado recebe automaticamente um ponto de conexão (endpoint) único.
                Quando uma ligação chegar, todos os agentes disponíveis serão notificados.
              </p>
            </div>
          </div>
        </div>

        <!-- Botão de atualização -->
        <div class="mt-6 pt-4 border-t border-n-slate-6">
          <NextButton
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
            :is-loading="isUpdating"
            :disabled="isUpdating || !hasSelectedAgents"
            @click="updateAgents"
          />
        </div>
      </div>
    </SettingsSection>
  </div>
</template>

<style scoped>
/* Estilos customizados para multiselect */
:deep(.voice-agents-multiselect) {
  .multiselect__tags {
    @apply bg-n-slate-1 border-n-slate-7 rounded-md min-h-[42px];
  }

  .multiselect__input,
  .multiselect__single {
    @apply bg-n-slate-1 text-n-slate-12;
  }

  .multiselect__placeholder {
    @apply text-n-slate-11;
  }

  .multiselect__content-wrapper {
    @apply bg-n-slate-1 border-n-slate-7 rounded-md;
  }

  .multiselect__option--highlight {
    @apply bg-n-iris-4 text-n-slate-12;
  }

  .multiselect__option--selected {
    @apply bg-n-iris-9 text-white font-medium;
  }

  .multiselect__tag {
    @apply bg-n-iris-9 text-white;
  }

  .multiselect__tag-icon:hover {
    @apply bg-n-iris-10;
  }
}
</style>